import classNames from 'classnames';
import { Fragment } from 'react';
import type { FunctionComponent, HTMLAttributes } from 'react';
import type { LiteralUnion } from 'type-fest';
import type { ThemeColorFragment } from '../../graphql/fragments/ThemeColorFragment.graphql';
import type { ThumbnailFragment } from '../../graphql/fragments/ThumbnailFragment.graphql';
import { iconSizeMedium } from '../../styles/variables.module.scss';
import type { ImageType } from '../../types/image';
import type { VideoType } from '../../types/video';
import { Anchor } from '../anchor';
import { Image } from '../image';
import { Skeleton } from '../skeleton';
import { ExternalLinkIcon } from '../svgs';
import { Text } from '../text';
import { Video } from '../video';
import styles from './thumbnail.module.scss';

/** Renders image and caption for whatever you want a preview of. */
export const Thumbnail: FunctionComponent<React.PropsWithChildren<Props>> = ({
  type,
  image,
  video,
  subtitle,
  title,
  href,
  target,
  fit = 'cover',
  size = 'medium',
  theme,
  openInNewWindow = false,
  ...restOfProps
}) => {
  const [Wrapper, wrapperProps] = href ? [Anchor, {
    href,
    target,
    preset: 'plain' as const
  }] : [Fragment, {} as any];
  return <Wrapper {...wrapperProps} data-sentry-element="Wrapper" data-sentry-component="Thumbnail" data-sentry-source-file="index.tsx">
            <figure tabIndex={href ? 0 : -1} aria-label={[title, subtitle].filter(Boolean).join(': ')} className={classNames(styles.figure, styles[size], styles[`${size}-${fit}`], {
      [styles.interactive]: href
    })} {...restOfProps}>
                <div className={classNames(styles['image-wrapper'])}>
                    <Preview video={video} image={image} size={size} fit={fit} data-sentry-element="Preview" data-sentry-source-file="index.tsx" />

                    {href && href?.indexOf('video') > -1 && <div className={styles.playBtnContainer}>
                            <img src='https://images.ctfassets.net/bkmkssdu4exw/3w2KW3Ag38BKAe0McPNtnn/e02ea4b4eef1889cb29e3b5fdddf4e93/play-o.png' alt='Play video' />
                        </div>}

                    {subtitle && type !== 'Icon' && <div className={styles.tagContainer}>
                            <div className={`${styles.subtitle} ${href && href?.indexOf('video') > -1 ? styles.watch : styles.read}`}>
                                <Text tag='div' typeStyle='body-md'>
                                    {href && href?.indexOf('video') > -1 ? 'Watch' : 'Read'}
                                </Text>
                            </div>

                            <div className={classNames(styles.subtitle, styles[`subtitle-color-future-fuscia`])}>
                                <Text tag='div' typeStyle='body-md' color='midnight'>
                                    {subtitle}
                                </Text>
                            </div>
                        </div>}
                </div>

                {title && <figcaption className={styles[`caption-${size}`]}>
                        <div className={styles.title}>
                            <Text typeStyle='body-md' tag='span'>
                                {title}
                            </Text>
                            {openInNewWindow && <ExternalLinkIcon />}
                        </div>
                    </figcaption>}

                {subtitle && type === 'Icon' && <Text tag='div' typeStyle='body-sm'>
                        {subtitle}
                    </Text>}
            </figure>
        </Wrapper>;
};
const Preview: FunctionComponent<React.PropsWithChildren<Pick<Props, 'size' | 'video' | 'image' | 'fit'>>> = ({
  size,
  image,
  video,
  fit
}) => {
  const width = size === 'medium' ? '100%' : iconSizeMedium;
  const className = classNames(styles.image, styles[`image-${size}`], styles[`image-${size}-${fit}`]);
  if (video) {
    return <Video {...video} className={className} preset={video.preset !== 'gif' ? 'video' : video.preset} width={width} sources={video.sources} poster={video.poster} />;
  }
  if (image) {
    return <Image width={width} fit={fit} className={className} source={image.source} source2x={image.source2x} source3x={image.source3x} contentType={image.contentType} alt={image.alt} />;
  }
  return <Skeleton className={styles['image-skeleton']} width={width} data-sentry-element="Skeleton" data-sentry-component="Preview" data-sentry-source-file="index.tsx" />;
};
interface Props extends Omit<HTMLAttributes<HTMLDivElement>, 'className'> {
  /** Is the content inside loading */
  loading?: boolean;
  /** Image to display */
  image?: ImageType;
  /** Image to display */
  video?: VideoType;
  /** Non-bold text above the title. */
  subtitle?: string;
  /** Bold text below the image. */
  title?: string;
  /** If this is passed, an anchor will be rendered. */
  href?: string;
  /** Anchor target. */
  target?: string;
  /** How the image fills its container.  */
  fit?: 'cover' | 'contain';
  /** Affects the size of the image and width of the container.  */
  size?: LiteralUnion<'small' | 'medium', string>;
  /** Theme to apply. */
  theme?: ThemeColorFragment;
  /** If true, the link will open in a new window. */
  openInNewWindow?: boolean;
  /** The type of thumbnail. */
  type?: ThumbnailFragment['type'];
}